import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    DConfirmationModal,
    DeleteIcon,
} from '../../../dum';
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    GetPayrollEntriesQuery,
    useGetPayrollEntriesQuery,
    useDeletePayrollEntryMutation,
    useUpsertPayrollEntryMutation,
    useOrderProductionEmployeeInputQuery,
} from '../../../../services';
import {
    formatFloat,
    handleTouchDoubleTap,
    TouchDoubleTapState,
} from '../../../../helpers';
import { PayrollEntriesExcelExportButton } from './payroll-entries-excel-export-button';

type TEntry = GetPayrollEntriesQuery['getPayrollEntries'][number];

// HE manual-override highlight (legacy Excel convention). Bright yellow reads well
// on a light theme but glares on dark, so use a muted dark mustard there.
const OVERRIDE_BG_LIGHT = '#fff59d';
const OVERRIDE_BG_DARK = '#5f5300';

// Columns *after* BD are colored by sign: additions (percepciones) blue,
// deductions (Total-) red. Theme tokens so both modes stay readable.
const ADD_COLOR = 'primary.main'; // blue
const SUB_COLOR = 'error.main'; // red

// ---------------------------------------------------------------------------
// Inline-editable row
// ---------------------------------------------------------------------------

interface IRowProps {
    entry: TEntry;
    employeeName: string;
    payrollPeriodId: number;
    onSaved: () => void;
    onDeleteClick: () => void;
}

interface IRowState {
    estatus: string;
    sueldo: number;
    jo: number;
    ht: number;
    he: number;
    he_override: boolean;
    retardos: number;
    faltas: number;
    vac: number;
    dias_festivos: number;
    bd: number;
    entregas_especiales: number;
    viajes: number;
    bonos: number;
    otros_mas: number;
    infonavit: number;
    fonacot: number;
    descuento_prestamos: number;
    otros_menos: number;
    observaciones: string;
}

function entryToRowState(e: TEntry): IRowState {
    return {
        estatus: e.estatus,
        sueldo: e.sueldo,
        jo: e.jo,
        ht: e.ht,
        he: e.he,
        he_override: e.he_override,
        retardos: e.retardos,
        faltas: e.faltas,
        vac: e.vac,
        dias_festivos: e.dias_festivos,
        bd: e.bd,
        entregas_especiales: e.entregas_especiales,
        viajes: e.viajes,
        bonos: e.bonos,
        otros_mas: e.otros_mas,
        infonavit: e.infonavit,
        fonacot: e.fonacot,
        descuento_prestamos: e.descuento_prestamos,
        otros_menos: e.otros_menos,
        observaciones: e.observaciones,
    };
}

function PayrollEntryRow({ entry, employeeName, payrollPeriodId, onSaved, onDeleteClick }: IRowProps) {
    const [row, setRow] = useState<IRowState>(() => entryToRowState(entry));
    const rowRef = useRef(row);
    const [editingField, setEditingField] = useState<keyof IRowState | null>(null);
    const touchDoubleTap = useRef<TouchDoubleTapState | null>(null);
    // Separate edit buffer for HE: when a cell is on the auto formula its text
    // starts blank (type a number to override; leave blank to stay auto).
    const [heText, setHeText] = useState('');

    useEffect(() => {
        const next = entryToRowState(entry);
        setRow(next);
        rowRef.current = next;
    }, [entry]);

    const [upsert] = useUpsertPayrollEntryMutation();

    const handleChange = (field: keyof IRowState, value: string) => {
        const parsed = typeof row[field] === 'number' ? (value === '' ? 0 : Number(value)) : value;
        const next = { ...rowRef.current, [field]: parsed };
        rowRef.current = next;
        setRow(next);
    };

    const persist = async () => {
        await upsert({
            variables: {
                PayrollEntryInput: {
                    id: entry.id,
                    payroll_period_id: payrollPeriodId,
                    employee_id: entry.employee_id,
                    control_bono_area: entry.control_bono_area,
                    control_deposito: entry.control_deposito,
                    ...rowRef.current,
                },
            },
        });
        onSaved();
    };

    const handleBlur = async () => {
        setEditingField(null);
        await persist();
    };

    // HE has no visible toggle: a non-empty value = manual override (cell yellow),
    // an empty value = revert to the auto formula.
    const handleHeBlur = async () => {
        setEditingField(null);
        const trimmed = heText.trim();
        let next: IRowState;
        if (trimmed === '') {
            next = { ...rowRef.current, he_override: false };
        } else {
            const num = Number(trimmed);
            if (Number.isNaN(num)) return; // invalid input → ignore, leave row as-is
            next = { ...rowRef.current, he_override: true, he: num };
        }
        rowRef.current = next;
        setRow(next);
        await persist();
    };

    const cell = (
        field: keyof IRowState,
        isNumber: boolean,
        align: 'left' | 'right' = 'right',
        color?: string,
    ) => {
        const isEditing = editingField === field;
        const displayValue = isNumber ? formatFloat(row[field] as number) : String(row[field]);
        return (
            <TableCell
                align={align}
                onDoubleClick={() => setEditingField(field)}
                onTouchEnd={(e) =>
                    handleTouchDoubleTap(
                        e,
                        touchDoubleTap,
                        () => setEditingField(field),
                        String(field),
                    )
                }
                sx={{
                    cursor: 'text',
                    overflow: 'hidden',
                    touchAction: 'manipulation',
                }}
            >
                {isEditing ? (
                    <TextField
                        autoFocus
                        fullWidth
                        value={row[field]}
                        size="small"
                        type="text"
                        variant="standard"
                        onChange={(e) => handleChange(field, e.target.value)}
                        onBlur={handleBlur}
                        inputProps={{
                            inputMode: isNumber ? 'decimal' : 'text',
                            style: { textAlign: align, padding: '2px 4px' },
                        }}
                    />
                ) : (
                    <Typography variant="body2" noWrap sx={{ textAlign: align, color }}>
                        {displayValue}
                    </Typography>
                )}
            </TableCell>
        );
    };

    const ro = (value: React.ReactNode, color?: string) => (
        <TableCell align="right">
            <Typography variant="body2" noWrap sx={{ textAlign: 'right', color }}>{value}</Typography>
        </TableCell>
    );

    // HE cell: always clickable. Shows the effective value (computed when auto,
    // the manual value when overridden). Yellow background marks a manual override,
    // mirroring the legacy Excel sheet. No separate "Auto" column.
    const heCell = () => {
        const isEditing = editingField === 'he';
        const displayValue = formatFloat(row.he_override ? row.he : entry.he_effective);
        return (
            <TableCell
                align="right"
                onDoubleClick={() => {
                    setHeText(row.he_override ? String(row.he) : '');
                    setEditingField('he');
                }}
                onTouchEnd={(e) =>
                    handleTouchDoubleTap(
                        e,
                        touchDoubleTap,
                        () => {
                            setHeText(
                                row.he_override ? String(row.he) : '',
                            );
                            setEditingField('he');
                        },
                        'he',
                    )
                }
                sx={{
                    cursor: 'text',
                    overflow: 'hidden',
                    touchAction: 'manipulation',
                    bgcolor: row.he_override
                        ? (theme) =>
                              theme.palette.mode === 'dark'
                                  ? OVERRIDE_BG_DARK
                                  : OVERRIDE_BG_LIGHT
                        : undefined,
                }}
            >
                {isEditing ? (
                    <TextField
                        autoFocus
                        fullWidth
                        value={heText}
                        placeholder={formatFloat(entry.he_effective)}
                        size="small"
                        type="text"
                        variant="standard"
                        onChange={(e) => setHeText(e.target.value)}
                        onBlur={handleHeBlur}
                        inputProps={{
                            inputMode: 'decimal',
                            style: { textAlign: 'right', padding: '2px 4px' },
                        }}
                    />
                ) : (
                    <Typography variant="body2" noWrap sx={{ textAlign: 'right' }}>
                        {displayValue}
                    </Typography>
                )}
            </TableCell>
        );
    };

    return (
        <TableRow>
            <TableCell align="left">
                <Typography variant="body2" noWrap sx={{ textAlign: 'left' }}>{employeeName}</Typography>
            </TableCell>
            {cell('estatus', false, 'left')}
            <TableCell align="left">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                    <Typography variant="body2" noWrap sx={{ textAlign: 'left' }}>{entry.area}</Typography>
                    {entry.is_leader ? (
                        <Tooltip title="Líder">
                            <Box component="span" sx={{ fontSize: '0.85rem', lineHeight: 1 }}>🎩</Box>
                        </Tooltip>
                    ) : null}
                </Box>
            </TableCell>
            {cell('sueldo', true)}
            {cell('jo', true)}
            {cell('ht', true)}
            {heCell()}
            {cell('retardos', true)}
            {cell('faltas', true)}
            {ro(formatFloat(entry.hn))}
            {ro(formatFloat(entry.he_pay))}
            {ro(formatFloat(entry.fest_pay))}
            {ro(formatFloat(entry.vac_pay))}
            {ro(formatFloat(entry.prima_vac))}
            {ro(formatFloat(entry.puntualidad))}
            {ro(formatFloat(entry.asistencia))}
            {ro(formatFloat(entry.bp))}
            {cell('bd', true)}
            {cell('entregas_especiales', true, 'right', ADD_COLOR)}
            {cell('viajes', true, 'right', ADD_COLOR)}
            {cell('bonos', true, 'right', ADD_COLOR)}
            {cell('otros_mas', true, 'right', ADD_COLOR)}
            {cell('infonavit', true, 'right', SUB_COLOR)}
            {cell('fonacot', true, 'right', SUB_COLOR)}
            {cell('descuento_prestamos', true, 'right', SUB_COLOR)}
            {cell('otros_menos', true, 'right', SUB_COLOR)}
            {ro(formatFloat(entry.total_plus), ADD_COLOR)}
            {ro(formatFloat(entry.total_minus), SUB_COLOR)}
            <TableCell align="right">
                <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'right' }}>
                    {formatFloat(entry.total)}
                </Typography>
            </TableCell>
            {cell('observaciones', false, 'left')}
            <TableCell>
                <IconButton size="small" onClick={onDeleteClick}>
                    <DeleteIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// Column widths in px — single source of truth for the fixed table layout.
// Order MUST match the cell order in PayrollEntryRow.
const BASE_COL_WIDTHS = [
    220, 60, 120,             // Empleado, Estatus, Área
    80, 50, 50, 50, 50, 50,   // Sueldo, JO, HT, HE, Ret., Falt.
    60, 60, 60, 60, 60, 60, 60, 60, // $HN, $HE, $Fest, $Vac, $PrimaV, Punt., Asist., BP
    60, 70, 60, 60, 60,       // BD, Entregas, Viajes, Bonos, Otros+
    70, 70, 70, 60,           // INFONAVIT, FONACOT, Préstamos, Otros-
    70, 70, 80, 140, 56,      // Total+, Total-, TOTAL, Obs., (acciones)
];

const COL_WIDTH_SCALE = 1.5;
const COL_WIDTHS = BASE_COL_WIDTHS.map((w) => Math.round(w * COL_WIDTH_SCALE));

const TABLE_WIDTH = COL_WIDTHS.reduce((a, b) => a + b, 0);

const HEADERS = [
    'Empleado', 'Estatus', 'Área',
    'Sueldo', 'JO', 'HT', 'HE', 'Ret.', 'Falt.',
    '$HN', '$HE', '$Fest', '$Vac', '$PrimaV', 'Punt.', 'Asist.', 'BP',
    'BD', 'Entregas', 'Viajes', 'Bonos', 'Otros+',
    'INFONAVIT', 'FONACOT', 'Préstamos', 'Otros-',
    'Total+', 'Total-', 'TOTAL', 'Obs.', '',
];

// Short explanation / formula per column. Order MUST match HEADERS.
// Calculated columns (marked $) show the formula from payroll-nomina-logic.md.
const HEADER_TOOLTIPS = [
    'Nombre del empleado',
    'Estatus laboral: ALTA / NUEVO INGRESO / BAJA / EXTRA',
    'Área — categoría del empleado (Bolseo / Extrusion / Mantenimiento / Choferes / Administrativo). 🎩 = líder',
    'Sueldo base semanal (MXN)',
    'JO — Jornada: horas estándar por semana (48 o 52.5)',
    'HT — Horas totales trabajadas (normales + extra)',
    'HE — Horas extra. Automática: si festivos>0 → max(0, HT + (JO/6)×festivos − JO); si no → max(0, HT − JO). Escribe un valor para sobrescribir (celda amarilla); bórralo para volver a la fórmula',
    'Retardos de la semana',
    'Faltas de la semana',
    '$HN — Pago horas normales. Si festivos>0 → (HT−HE)/JO × Sueldo; si no → Sueldo si HT/JO>1, si no (HT/JO)×Sueldo',
    '$HE — Pago horas extra (2×): (Sueldo/7)/(JO/6) × HE × 2',
    '$Fest — Pago días festivos: (Sueldo/7) × días festivos',
    '$Vac — Pago vacaciones (tarifa diaria): (Sueldo/7) × VAC',
    '$PrimaV — Prima vacacional (25%): (Sueldo/7) × VAC × 0.25',
    'Puntualidad: 0 si hay faltas o es Supervisor; si no → $HN × 18%',
    'Asistencia: 0 si hay faltas o es Supervisor; si no → $HN × 18%',
    'BP — Bono producción (Estrusión): Sueldo × 20% si área=Estrusión y control bono=SÍ; si no 0',
    'BD — Bono disponibilidad (manual): 8h=$100, 16h=$150, 24h=$200',
    'Entregas especiales (bono manual)',
    'Viajes (bono manual)',
    'Otros bonos (manual)',
    'Otras percepciones (manual)',
    'INFONAVIT — descuento de vivienda (fijo, manual)',
    'FONACOT — descuento de crédito al consumo (fijo, manual)',
    'Descuento de préstamos o adelantos',
    'Otras deducciones',
    'Total+ — Percepciones: $HN+$HE+$Fest+$Vac+$PrimaV+Punt+Asist+BP+BD+Entregas+Viajes+Bonos+Otros+',
    'Total− — Deducciones: INFONAVIT+FONACOT+Préstamos+Otros−',
    'TOTAL — Neto a pagar: Total+ − Total−',
    'Observaciones / notas',
    '',
];

// Text (non-numeric) columns are left-aligned in both header and cells.
// Everything else is numeric/money and right-aligns. Indices match HEADERS.
const TEXT_COLS = new Set([0, 1, 2, 29]); // Empleado, Estatus, Área, Obs.

export function PayrollEntriesPage() {
    const { periodId } = useParams<{ periodId: string }>();
    const numericPeriodId = Number(periodId);

    const [deleteTarget, setDeleteTarget] = useState<TEntry | null>(null);

    const { data, refetch } = useGetPayrollEntriesQuery({
        variables: { PayrollPeriodId: numericPeriodId },
        skip: !numericPeriodId,
    });

    const [deleteMutation] = useDeletePayrollEntryMutation();

    const { data: employeeData } = useOrderProductionEmployeeInputQuery({
        variables: { excludeDiscontinued: true },
    });

    const employees = employeeData?.getEmployees ?? [];
    const employeeName = (id: number) =>
        employees.find((e) => e.id === id)?.fullname ?? String(id);

    const entries = data?.getPayrollEntries ?? [];
    const grandTotal = entries.reduce((sum, e) => sum + e.total, 0);

    const handleDeleteConfirmed = async () => {
        if (!deleteTarget) return;
        await deleteMutation({ variables: { PayrollEntryId: deleteTarget.id } });
        setDeleteTarget(null);
        await refetch();
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
                <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                    <Table
                        stickyHeader
                        size="small"
                        sx={{ tableLayout: 'fixed', width: TABLE_WIDTH, minWidth: TABLE_WIDTH }}
                    >
                        <colgroup>
                            {COL_WIDTHS.map((w, i) => (
                                <col key={i} style={{ width: w }} />
                            ))}
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                {HEADERS.map((h, i) => (
                                    <TableCell
                                        key={i}
                                        align={TEXT_COLS.has(i) ? 'left' : 'right'}
                                        sx={{ bgcolor: 'background.paper' }}
                                    >
                                        {HEADER_TOOLTIPS[i] ? (
                                            <Tooltip title={HEADER_TOOLTIPS[i]} arrow placement="top">
                                                <span style={{ cursor: 'help' }}>{h}</span>
                                            </Tooltip>
                                        ) : (
                                            h
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.map((entry) => (
                                <PayrollEntryRow
                                    key={entry.id}
                                    entry={entry}
                                    employeeName={employeeName(entry.employee_id)}
                                    payrollPeriodId={numericPeriodId}
                                    onSaved={() => refetch()}
                                    onDeleteClick={() => setDeleteTarget(entry)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 1,
                        flexShrink: 0,
                    }}
                >
                    <PayrollEntriesExcelExportButton
                        entries={entries}
                        employeeName={employeeName}
                        grandTotal={grandTotal}
                        fileName={`nomina-periodo-${numericPeriodId}.xlsx`}
                    />
                    <Typography variant="h6">
                        Gran Total: {formatFloat(grandTotal)}
                    </Typography>
                </Box>
            </Box>
            <DConfirmationModal
                open={!!deleteTarget}
                setOpen={(open) => { if (!open) setDeleteTarget(null); }}
                callback={handleDeleteConfirmed}
            />
        </Box>
    );
}
